import { type SummaryItem } from '@features/add-to-cart-page/components/SummaryStore/SummaryStore.types';
import type {
  ProductSummary,
  SecondChanceProduct
} from '@features/add-to-cart-page/typings/Product';
import { type EnrichedShoppingCartItem } from '@features/add-to-cart-page/typings/ShoppingCart';

import { type ProductPriceData, ProductPriceProps } from './ProductPrice.types';

function getConfiguratorTotal(
  configuratorItems: EnrichedShoppingCartItem[],
  priceType: 'includingVat' | 'excludingVat' = 'includingVat'
): number {
  return configuratorItems.reduce(
    (sum, item) => sum + item.shoppingCartItemPrice[priceType] * item.quantity,
    0
  );
}

export function calculateTotalPrice(
  basePrice: number,
  configuratorItems: EnrichedShoppingCartItem[]
): number {
  if (configuratorItems.length === 0) {
    return basePrice;
  }

  return basePrice + getConfiguratorTotal(configuratorItems, 'includingVat');
}

export function calculateBusinessPrice(
  basePrice: number,
  quantity: number,
  configuratorItems: EnrichedShoppingCartItem[]
): number {
  if (!basePrice) return 0;

  const productTotal = basePrice * quantity;

  if (!configuratorItems || configuratorItems.length === 0) {
    return productTotal;
  }

  return productTotal + getConfiguratorTotal(configuratorItems, 'includingVat');
}

export function calculateFormerPrice(
  product: ProductPriceData,
  configuratorItems: EnrichedShoppingCartItem[]
): number {
  const listPrice = product.listPrice?.includingVat;
  const formerPrice = listPrice || product.priceIncludingVat;

  if (!configuratorItems.length && formerPrice !== listPrice) {
    return 0;
  }

  const configuratorTotal = configuratorItems.reduce(
    (sum, item) => sum + item.shoppingCartItemProductPrice.includingVat * item.quantity,
    0
  );

  return formerPrice + configuratorTotal;
}

export function calculateFormerPriceLabel(
  formerPriceLabel: string | undefined,
  hasSubProducts: boolean,
  hasConfiguratorItems: boolean,
  defaultLabel: string
): string | undefined {
  if (formerPriceLabel) {
    return formerPriceLabel;
  }
  return hasSubProducts || hasConfiguratorItems ? undefined : defaultLabel;
}

function computePersonalPriceMultiplier(
  basePrice: { includingVat: number; excludingVat?: number },
  quantity: number
): { includingVat: number; excludingVat?: number } {
  return {
    includingVat: (basePrice.includingVat ?? 0) * quantity,
    excludingVat:
      basePrice.excludingVat !== undefined ? basePrice.excludingVat * quantity : undefined
  };
}

export function mergePersonalPriceWithConfigurator(
  basePrice: { includingVat: number; excludingVat?: number },
  configuratorItems: EnrichedShoppingCartItem[],
  quantity: number
): { includingVat: number; excludingVat?: number } {
  const includingVatTotal =
    basePrice.includingVat * quantity + getConfiguratorTotal(configuratorItems, 'includingVat');
  const excludingVatTotal =
    basePrice.excludingVat !== undefined
      ? basePrice.excludingVat * quantity + getConfiguratorTotal(configuratorItems, 'excludingVat')
      : undefined;

  return {
    includingVat: includingVatTotal,
    excludingVat: excludingVatTotal
  };
}

export function calculatePersonalPrice(
  product: ProductPriceData,
  configuratorItems: EnrichedShoppingCartItem[],
  quantity: number = 1
):
  | {
      price: {
        includingVat: number;
        excludingVat?: number;
      };
      labelText: string;
    }
  | undefined {
  if (!product.personalPrice) {
    return undefined;
  }

  const priceData =
    configuratorItems.length === 0
      ? computePersonalPriceMultiplier(product.personalPrice.price, quantity)
      : mergePersonalPriceWithConfigurator(
          product.personalPrice.price,
          configuratorItems,
          quantity
        );

  return {
    price: priceData,
    labelText: product.personalPrice.labelText
  };
}

export function calculateSecondChanceTotalPrice(
  secondChanceProduct: SecondChanceProduct,
  configuratorItems: EnrichedShoppingCartItem[]
): number {
  return calculateTotalPrice(secondChanceProduct.price.includingVat, configuratorItems);
}

export function mapComplementaryItemToProductPriceData(summaryItem: SummaryItem): ProductPriceData {
  return {
    id: !isNaN(Number(summaryItem.id)) ? Number(summaryItem.id) : undefined,
    name: summaryItem.name,
    brand: summaryItem.brand,
    priceIncludingVat: summaryItem.salesPrice.includingVat,
    priceExcludingVat: summaryItem.salesPrice.excludingVat,
    subscription: undefined,
    secondChanceProduct: undefined,
    personalPrice: summaryItem.personalPrice,
    paymentType: summaryItem.paymentType,
    listPrice: summaryItem.listPrice
      ? {
          includingVat: summaryItem.listPrice.includingVat,
          excludingVat: summaryItem.listPrice.excludingVat
        }
      : undefined,
    hasSubProducts: false
  };
}

export function mapMainProductToProductPriceData(
  productSummary: ProductSummary,
  _quantity: number
): ProductPriceData {
  const priceIncludingVat = productSummary.priceIncludingVat;
  const priceExcludingVat = productSummary.priceExcludingVat;

  const listPrice = productSummary.listPriceIncludingVat
    ? {
        includingVat: productSummary.listPriceIncludingVat,
        excludingVat: undefined
      }
    : undefined;

  return {
    id: Number(productSummary.id),
    name: productSummary.name,
    brand: productSummary.brand,
    priceIncludingVat,
    priceExcludingVat,
    subscription: productSummary.subscription,
    secondChanceProduct: productSummary.secondChanceProduct,
    personalPrice: productSummary.personalPrice,
    paymentType: undefined,
    listPrice,
    hasSubProducts: (productSummary.subProducts?.length ?? 0) > 0
  };
}

export function mapPriceDataToProductPriceProps(props: ProductPriceProps): ProductPriceData {
  if (props.itemType === 'complementary') {
    return mapComplementaryItemToProductPriceData(props.product as SummaryItem);
  }

  return mapMainProductToProductPriceData(props.product as ProductSummary, props.quantity);
}

export function mapQuantity(props: ProductPriceProps): number {
  if (props.itemType === 'main' && props.isBusinessCustomer) {
    return props.quantity || 1;
  }

  if ('quantity' in props.product) {
    return props.product.quantity || 1;
  }

  return 1;
}
